// src/pages/Documentation.jsx
import React from "react";
import { FileText, HelpCircle, Settings } from "lucide-react";
import { Card } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const Documentation = () => {
  return (
    <div className="flex-grow px-4 md:px-6 py-6 bg-bg-secondary min-h-screen">
      <div className="max-w-5xl mx-auto">
        <Card className="p-6 md:p-8 shadow-lg">
          {/* Header */}
          <div className="flex items-center mb-4">
            <FileText className="w-9 h-9 text-primary-600 mr-3" />
            <h1 className="text-3xl font-extrabold text-text-primary">
              Documentation
            </h1>
          </div>

          <p className="mb-6 text-text-secondary">
            Welcome to the TestNimbus Documentation center. Here you can find
            detailed guides, step-by-step tutorials, and troubleshooting
            information for all features in the system.
          </p>

          <Separator className="mb-6" />

          {/* Quick Access Links */}
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3 text-primary-600">
              Quick Access
            </h3>
            <div className="flex flex-wrap gap-2">
              <Badge
                variant="outline"
                className="cursor-pointer border-primary-600 text-primary-600 hover:bg-primary-50 px-3 py-1"
                onClick={() => window.open("#getting-started", "_self")}
              >
                Getting Started
              </Badge>
              <Badge
                variant="outline"
                className="cursor-pointer border-primary-600 text-primary-600 hover:bg-primary-50 px-3 py-1"
                onClick={() => window.open("#client-management", "_self")}
              >
                Managing Clients
              </Badge>
              <Badge
                variant="outline"
                className="cursor-pointer border-primary-600 text-primary-600 hover:bg-primary-50 px-3 py-1"
                onClick={() => window.open("#project-setup", "_self")}
              >
                Project Setup
              </Badge>
              <Badge
                variant="outline"
                className="cursor-pointer border-primary-600 text-primary-600 hover:bg-primary-50 px-3 py-1"
                onClick={() => window.open("#account-settings", "_self")}
              >
                Account Settings
              </Badge>
            </div>
          </div>

          {/* Accordion Sections */}
          <Accordion
            type="single"
            collapsible
            defaultValue="getting-started"
            className="w-full"
          >
            <AccordionItem value="getting-started" id="getting-started">
              <AccordionTrigger className="font-bold text-text-primary">
                1. Getting Started
              </AccordionTrigger>
              <AccordionContent className="leading-relaxed text-text-primary">
                To get started with TestNimbus:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>
                    Log in with your credentials using the secure login page.
                  </li>
                  <li>Chief admins can create clients and assign users.</li>
                  <li>
                    Other users will see projects assigned to them directly.
                  </li>
                </ul>
                For more details, visit our{" "}
                <a
                  href="https://testnimbus.com/docs/getting-started"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline"
                >
                  Getting Started Guide
                </a>
                .
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="client-management" id="client-management">
              <AccordionTrigger className="font-bold text-text-primary">
                2. Client Management
              </AccordionTrigger>
              <AccordionContent className="leading-relaxed text-text-primary">
                Chief Admins can:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Create, edit, and deactivate client accounts.</li>
                  <li>Assign users and manage client permissions.</li>
                  <li>Link projects to client accounts for collaboration.</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="project-setup" id="project-setup">
              <AccordionTrigger className="font-bold text-text-primary">
                3. Project Setup
              </AccordionTrigger>
              <AccordionContent className="leading-relaxed text-text-primary">
                To set up a project:
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Click on "Add Project" from the Projects section.</li>
                  <li>
                    Fill in details like name, description, and client
                    association.
                  </li>
                  <li>Upload templates, data, and assign responsible users.</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="account-settings" id="account-settings">
              <AccordionTrigger className="font-bold text-text-primary">
                4. Account & Security Settings
              </AccordionTrigger>
              <AccordionContent className="leading-relaxed text-text-primary">
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>
                    View and edit your profile in "My Account → My Profile".
                  </li>
                  <li>
                    Change your password regularly under "Change Password".
                  </li>
                  <li>
                    Use Two-Factor Authentication (2FA) for enhanced security.
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <Separator className="my-6" />

          {/* Support Section */}
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary-600" />
            <p className="text-sm text-text-secondary">
              Need more help? Visit our{" "}
              <a href="/support" className="text-primary-600 hover:underline">
                Support Page
              </a>{" "}
              or contact your system administrator.
            </p>
          </div>

          <div className="flex justify-end mt-6 text-xs text-text-tertiary">
            <Settings className="w-4 h-4 mr-1" />
            <span>TestNimbus Docs v1.0</span>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Documentation;
